import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { CalendarPlus, Check, X, Trash2 } from "lucide-react";

const STATUS = {
  pending: ["Σε αναμονή", "bg-[#FFFBEB] text-[#92400E]"],
  approved: ["Εγκρίθηκε", "bg-[#EEF0EA] text-[#6E7A63]"],
  rejected: ["Απορρίφθηκε", "bg-[#FEF2F2] text-[#991B1B]"],
};

export default function Leave() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canDecide = ["owner", "hr", "manager"].includes(user?.role);
  const today = new Date().toISOString().slice(0, 10);
  const forEmployee = ["owner", "hr"].includes(user?.role) ? new URLSearchParams(window.location.search).get("employee") : null;
  const [form, setForm] = useState({ type: "annual", start_date: today, end_date: today, note: "", ...(forEmployee ? { employee_name: forEmployee } : {}) });

  const { data: types = [] } = useQuery({ queryKey: ["leave-types"], queryFn: async () => (await api.get("/leave/types")).data });
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["leave"], queryFn: async () => (await api.get("/leave")).data });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["leave"] });
    qc.invalidateQueries({ queryKey: ["notif-count"] });
  };
  const create = useMutation({
    mutationFn: async () => (await api.post("/leave", form)).data,
    onSuccess: () => { refresh(); toast.success("Η αίτηση άδειας στάλθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const decide = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/leave/${id}/decide?status=${status}`)).data,
    onSuccess: (d) => { refresh(); toast.success(d.status === "approved" ? "Η άδεια εγκρίθηκε" : "Η άδεια απορρίφθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/leave/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Η αίτηση ακυρώθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const inp = "mt-1 w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
  const lab = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold";

  return (
    <div className="space-y-6" data-testid="leave-page">
      {forEmployee && (
        <div data-testid="leave-for-employee" className="rounded-xl bg-[#F3EADF] border border-[#E3DED4] px-4 py-2.5 text-sm text-[#45423C]">
          Νέα άδεια για: <b>{forEmployee}</b> — συμπληρώστε τις ημερομηνίες και πατήστε Υποβολή.
        </div>
      )}
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Προσωπικό</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Άδειες</h1>
        <p className="text-[#6F6A60] text-sm mt-1">Υποβολή αίτησης από το κινητό — έγκριση από manager, HR ή ιδιοκτήτη.</p>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="leave-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className={lab}>Τύπος</label>
            <select data-testid="leave-type" className={inp} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className={lab}>Από</label>
            <input data-testid="leave-start" type="date" className={inp} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className={lab}>Έως</label>
            <input data-testid="leave-end" type="date" className={inp} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
          <div className="lg:col-span-1">
            <label className={lab}>Σημείωση</label>
            <input data-testid="leave-note" className={inp} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="προαιρετικό" />
          </div>
          <div className="flex items-end">
            <button data-testid="leave-submit" onClick={() => create.mutate()} disabled={create.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50 w-full justify-center">
              <CalendarPlus className="h-4 w-4" /> {create.isPending ? "Αποστολή…" : "Υποβολή"}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4]">
          <h2 className="font-medium text-[#45423C]">{canDecide ? "Όλες οι αιτήσεις" : "Οι αιτήσεις μου"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Υπάλληλος</th>
                <th className="px-4 py-3">Τύπος</th>
                <th className="px-4 py-3">Από</th>
                <th className="px-4 py-3">Έως</th>
                <th className="px-4 py-3 text-center">Ημέρες</th>
                <th className="px-4 py-3">Κατάσταση</th>
                <th className="px-4 py-3">Σημείωση</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {rows.map((r) => {
                const [label, cls] = STATUS[r.status] || STATUS.pending;
                return (
                  <tr key={r.id} data-testid={`leave-row-${r.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                    <td className="px-4 py-2 font-medium text-[#45423C]">{r.employee_name}</td>
                    <td className="px-4 py-2 text-[#6F6A60]">{r.type_label}</td>
                    <td className="px-4 py-2 tabular">{fmtDate(r.start_date)}</td>
                    <td className="px-4 py-2 tabular">{fmtDate(r.end_date)}</td>
                    <td className="px-4 py-2 text-center tabular">{r.days}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>{label}</span>
                      {r.decided_by && <span className="ml-2 text-[10px] text-[#6F6A60]">{r.decided_by}</span>}
                    </td>
                    <td className="px-4 py-2 text-[#6F6A60] max-w-[240px] truncate" title={r.note}>{r.note || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        {canDecide && r.status === "pending" && (
                          <>
                            <button data-testid={`leave-approve-${r.id}`} onClick={() => decide.mutate({ id: r.id, status: "approved" })}
                              className="inline-flex items-center gap-1 rounded-lg bg-[#6E7A63] text-white px-2.5 py-1.5 text-xs font-semibold hover:opacity-90">
                              <Check className="h-3.5 w-3.5" /> Έγκριση
                            </button>
                            <button data-testid={`leave-reject-${r.id}`} onClick={() => decide.mutate({ id: r.id, status: "rejected" })}
                              className="inline-flex items-center gap-1 rounded-lg border border-[#E3DED4] px-2.5 py-1.5 text-xs font-semibold text-[#991B1B] hover:border-[#991B1B]">
                              <X className="h-3.5 w-3.5" /> Απόρριψη
                            </button>
                          </>
                        )}
                        {r.status === "pending" && (
                          <button data-testid={`leave-cancel-${r.id}`} onClick={() => remove.mutate(r.id)}
                            className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-[#6F6A60]">Καμία αίτηση άδειας.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
